/**
 * interpretTimelineResponse — classify a `/scheduler/(multi-day-)timeline` response so
 * every consumer treats the four cases the same way (#1309b).
 *
 * The server now returns a `computing` placeholder on a cold cache miss (it solves in the
 * background instead of blocking the request). Consumers must NOT mistake that for an
 * error or for a genuinely empty night — hence a single shared, flag-driven classifier:
 *
 *   - 'error'      — the request failed (`!ok`) or the scheduler reported failure
 *                    (`success === false`). Show an error; keep prior data if you have it.
 *   - 'computing'  — ONLY when `body.computing === true`. Show "Computing…" and re-poll;
 *                    never derive this from emptiness (a full-moon / all-done night is
 *                    legitimately empty and must NOT trigger a re-poll).
 *   - 'empty'      — a successful response with zero nights and no `computing` flag.
 *                    Show "No observations scheduled".
 *   - 'data'       — a successful response with at least one night.
 *
 * Pure and null-safe: never throws on a malformed body. The timeline lives at
 * `body.data.nights` for the multi-day endpoint (the only one with GUI consumers).
 *
 * @param {boolean} ok    the HTTP response's `ok` (2xx) flag
 * @param {any} body      the parsed JSON body (may be undefined)
 * @returns {{status:'error'|'computing'|'empty'|'data', message?:string, nights?:any[], data?:any}}
 */
export function interpretTimelineResponse(ok, body) {
  if (!ok || !body || body.success === false) {
    return { status: 'error', message: (body && body.error) || 'Failed to load timeline' };
  }
  if (body.computing === true) {
    return { status: 'computing' };
  }
  const nights = body.data && body.data.nights;
  if (!Array.isArray(nights) || nights.length === 0) {
    return { status: 'empty' };
  }
  return { status: 'data', nights, data: body.data };
}
